import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Pagination,
  Skeleton,
  Chip,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ErrorPage from "../Components/ErrorPage";
import DebtorsAdd from "./DebtorsAdd";
import DebtorsEdit from "./DebtorsEdit";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

const DebtorsIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const query = new URLSearchParams();
  if (searchTerm) query.append("search", searchTerm);
  query.append("page", page);
  query.append("page_size", rowsPerPage);
  const fetchUrl = `shop/customer-debtors/?${query.toString()}`;

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["customer-debtors", searchTerm, page, rowsPerPage],
    fetchUrl,
    token
  );
  const { handleDelete, ConfirmDialog } = useDelete(
    "shop/customer-debtors",
    token,
    refetch
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const debtors = data?.results ?? data ?? [];
  const totalCount = data?.count ?? debtors.length;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  if (isError) {
    return (
      <ErrorPage
        message={t("debtor.failedToLoad", "Failed to load debtors.")}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h4">
            {t("debtor.title", "Debtors")}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t("debtor.dataLoadedOn", "Data loaded on")}{" "}
            {new Date().toLocaleString()}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenAdd(true)}
        >
          {t("debtor.addDebtor", "Give money to customer")}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          "debtor.helpText",
          "Give money to a customer. If you select an account, the amount is deducted from that account. If you leave account empty, only the customer balance increases. When the customer pays later, it reduces this debtor balance."
        )}
      </Alert>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("debtor.search", "Search debtors...")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="debtors table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>#</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.customer", "Customer")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.account", "Account")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.currency", "Currency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.amount", "Amount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.settled", "Settled")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.outstanding", "Outstanding")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.exchangeRate", "Exchange Rate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.convertedAmount", "Converted")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.loanDate", "Date")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.notes", "Notes")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.action", "Action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from(new Array(12)).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : debtors.map((row, i) => {
                  const index = (page - 1) * rowsPerPage + i + 1;
                  const outstanding =
                    row.outstanding_amount ??
                    Number(row.amount || 0) - Number(row.settled_amount || 0);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{index}</TableCell>
                      <TableCell>
                        {row?.customer_details?.name ?? "—"}
                      </TableCell>
                      <TableCell>
                        {row?.account_details?.name ?? "—"}
                      </TableCell>
                      <TableCell>
                        {row?.currency_details?.title ?? "—"}
                      </TableCell>
                      <TableCell>{row.amount}</TableCell>
                      <TableCell>{row.settled_amount ?? 0}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={Number(outstanding) > 0 ? "warning" : "success"}
                          label={Number(outstanding).toFixed(2)}
                        />
                      </TableCell>
                      <TableCell>{row.exchange_rate ?? "—"}</TableCell>
                      <TableCell>{row.converted_amount ?? "—"}</TableCell>
                      <TableCell>
                        {row.loan_date
                          ? new Date(row.loan_date).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell>{row.notes || "—"}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => {
                            setEditData(row);
                            setOpenEdit(true);
                          }}
                        >
                          {t("debtor.edit", "Edit")}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(row.id)}
                        >
                          {t("debtor.delete", "Delete")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container justifyContent="center" alignItems="center" sx={{ p: 2 }}>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage) || 1}
          page={page}
          onChange={(e, newPage) => setPage(newPage)}
          variant="outlined"
          color="primary"
        />
      </Grid>

      <ConfirmDialog />
      {openAdd && (
        <DebtorsAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
          onSuccess={refetch}
        />
      )}
      {openEdit && editData && (
        <DebtorsEdit
          open={openEdit}
          onClose={() => {
            setOpenEdit(false);
            setEditData(null);
          }}
          editData={editData}
          token={token}
          onSuccess={refetch}
        />
      )}
    </Box>
  );
};

export default DebtorsIndex;
